import React, { ReactElement, useMemo } from 'react';
import { connect } from 'react-redux';
import { MapViewer, MapViewerControls } from '..';
import { MapDetailDto } from '../../../models';
import { convertToLatLng, extractPrefix, getMapTitle } from '../../../utils';
import {
	AppState,
	AppThunkDispatch,
	LegendItem,
	legendStringToLegendItems,
	MapAction,
	Project
} from '../../../redux';
import { BackButton, DialogWindow, MapDrawing } from '../../molecules';

import './project-preview-dialog.styles.scss';
import { useStateMap } from '../../../hooks';
import { ErrorBoundary, Header5 } from '../../atoms';
import { Box } from '@material-ui/core';

export interface IProjectPreviewDialogStateProps {
	getDispatch: () => AppThunkDispatch<MapAction>;
}

export interface IProjectPreviewDialogProps {
	open: boolean;
	project?: Project;
	onClose: () => void;
}

export function ProjectPreviewDialogView(
	props: IProjectPreviewDialogProps & IProjectPreviewDialogStateProps
): ReactElement {
	const { open, project, onClose, getDispatch } = props;

	const map: MapDetailDto | undefined = useStateMap(
		project?.metaDataId,
		getDispatch()
	);

	const legendItems: LegendItem[] | undefined = useMemo<
		LegendItem[] | undefined
	>(() => {
		if (!map) return undefined;
		return map.legend ? legendStringToLegendItems(map.legend) : undefined;
	}, [map]);

	function getTitle(): string {
		if (project && project.title) return project.title;
		if (map && map.title) return getMapTitle(map);
		return '';
	}

	return (
		<DialogWindow open={open} fullScreen className="project-preview-dialog">
			<Box className="project-preview-header">
				<BackButton onClick={onClose} />
				<Header5 className="project-preview-title">
					{getTitle()}
				</Header5>
			</Box>
			{map && map.imageUrl && project && (
				<ErrorBoundary>
					<MapViewer
						mapId={map.id}
						casperId={map.casperId as string}
						corner={convertToLatLng(map.corner)}
						oppositeCorner={convertToLatLng(map.oppositeCorner)}
						projection={map.projection}
					>
						<MapViewerControls
							prefix={extractPrefix(map.casperId as string)}
							mapId={map.id}
							corner={convertToLatLng(map.corner)}
							oppositeCorner={convertToLatLng(map.oppositeCorner)}
							attachmentCount={project.attachments.length}
							options={{
								visible: true,
								drawingControls: false,
								mapActions: false
							}}
							legendItems={legendItems}
							legendUrl={map.legendUrl}
						/>
						<MapDrawing
							drawings={project.drawings}
							attachments={project.attachments}
							showDrawings={true}
							removePinLocation={() => {}}
						/>
					</MapViewer>
				</ErrorBoundary>
			)}
		</DialogWindow>
	);
}

function mapStateToProps(state: AppState, props: IProjectPreviewDialogProps) {
	return {
		map: props.project
			? state.maps.loadedMaps[props.project.metaDataId]
			: undefined
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapAction>) {
	return {
		getDispatch: () => dispatch
	};
}

export const ProjectPreviewDialog = connect(
	mapStateToProps,
	mapDispatchToProps
)(ProjectPreviewDialogView);
